import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { type Job, type NurseData, type NurseRowBadge, NurseTrainingStatus } from "@/utils/models";
import type { ShiftAvailabilityBadge, ShiftAvailabilityWindow, ShiftUnavailableReason } from "@/utils/shiftAvailability";
import { getShiftIndexForWindow, type ShiftOptionWindow } from "@/utils/shiftOptionWindows";

/* ── Shift details (the footer chip row) ─────────────────────────────────────────────────────────── */

/** One choice in the "Shift details" row — charge and the training statuses are mutually exclusive by design. */
export const SHIFT_DETAILS = ["none", "charge", "trainee", "orientation", "education"] as const;
export type ShiftDetail = (typeof SHIFT_DETAILS)[number];

/** What a shift detail writes onto the added shift. */
export type ShiftAssignment = { charge: boolean; trainingStatus: NurseTrainingStatus };

const DETAIL_TRAINING_STATUS: Record<ShiftDetail, NurseTrainingStatus> = {
	none: NurseTrainingStatus.Regular,
	charge: NurseTrainingStatus.Regular,
	trainee: NurseTrainingStatus.Trainee,
	orientation: NurseTrainingStatus.Orientation,
	education: NurseTrainingStatus.Education,
};

/** Charge ⇒ a regular-status charge shift; a training status ⇒ never charge. */
export const shiftDetailToAssignment = (detail: ShiftDetail): ShiftAssignment => ({
	charge: detail === "charge",
	trainingStatus: DETAIL_TRAINING_STATUS[detail],
});

/** i18n key of each detail chip's label. */
export const SHIFT_DETAIL_LABEL_KEYS: Record<ShiftDetail, string> = {
	none: "add.staff.detail.none",
	charge: "add.staff.detail.charge",
	trainee: "add.staff.detail.trainee",
	orientation: "add.staff.detail.orientation",
	education: "add.staff.detail.education",
};

/**
 * Chip test ids. Charge and the training statuses keep the ids their old switches carried, so the
 * existing e2e clicks still land on the control that does the same thing.
 */
export const SHIFT_DETAIL_TEST_IDS: Record<ShiftDetail, string> = {
	none: "add-nurse-detail-none",
	charge: "add-nurse-make-charge-switch",
	trainee: "add-nurse-trainee-switch",
	orientation: "add-nurse-orientation-switch",
	education: "add-nurse-education-switch",
};

/** One pick = the single add (today's dialogs, the callers' callbacks); more = the multi-add. */
export const SINGLE_ADD_COUNT = 1;

/* ── Windows, hours and dates ─────────────────────────────────────────────────────────────────────── */

/** "3:00 PM" — a non-breaking space keeps the meridiem on the figure's line when a card narrows. */
const CLOCK_FORMAT = "h:mm a";
const EN_DASH = "–";
const DATE_FORMAT = "ccc, MMM d";
const HOURS_PRECISION = 100;

/** "3:00 PM–11:00 PM" — the one spelling of a shift window across cards, header, reasons and summary. */
export const formatShiftHours = (window: ShiftAvailabilityWindow): string =>
	`${window.start.toFormat(CLOCK_FORMAT)}${EN_DASH}${window.end.toFormat(CLOCK_FORMAT)}`;

/** "Tue, Sep 22". */
export const formatShiftDate = (date: DateTime): string => date.toFormat(DATE_FORMAT);

/** Hours rounded to two decimals, so 7.499999 reads as 7.5 and 8 stays 8. */
export const roundHours = (hours: number): number => Math.round(hours * HOURS_PRECISION) / HOURS_PRECISION;

export const isSameWindow = (a: ShiftAvailabilityWindow, b: ShiftAvailabilityWindow): boolean =>
	a.start.toMillis() === b.start.toMillis() && a.end.toMillis() === b.end.toMillis();

/**
 * The same wall-clock hours moved from one staffing day to another: a card picked on Sat keeps its
 * 7:00 AM–7:00 PM when the panel is re-targeted to Sun. Whole calendar days, so a DST change keeps the hours.
 */
export const rebaseWindowToDate = (window: ShiftAvailabilityWindow, from: DateTime, to: DateTime): ShiftAvailabilityWindow => {
	const days = Math.round(to.startOf("day").diff(from.startOf("day"), "days").days);
	if (days === 0) return window;
	return { start: window.start.plus({ days }), end: window.end.plus({ days }) };
};

/* ── Re-targeting the panel to another date ───────────────────────────────────────────────────────── */

type RetargetGate = { allowDateChange: boolean; lockedTimes?: { startTime: DateTime; endTime: DateTime }; hideRoomSelect: boolean; showActual: boolean };

/**
 * Whether the header's date may move the panel. The caller must opt in, and never for a request's locked
 * shift, the hospital hand-off (its callback lands on its own day) or worked hours (tied to the worked day).
 */
export const canRetargetPanelDate = ({ allowDateChange, lockedTimes, hideRoomSelect, showActual }: RetargetGate): boolean =>
	allowDateChange && !lockedTimes && !hideRoomSelect && !showActual;

/** Where a date sits in the loaded `hospitalData.weeks`: the data-week and the day inside it. */
export type LoadedDay = { weekIndex: number; dayNo: number };

/** The provider's date lookups (both `-1` for a date outside the loaded weeks). */
export type LoadedDayLookup = { getWeekIndexByDate: (date: DateTime) => number; getDayIndexByDate: (date: DateTime) => number };

/** The loaded data-week + day of `date`, or `null` when it isn't loaded (the picker's bounds should prevent that). */
export const resolveLoadedDay = (date: DateTime, lookup: LoadedDayLookup): LoadedDay | null => {
	const weekIndex = lookup.getWeekIndexByDate(date);
	const dayNo = lookup.getDayIndexByDate(date);
	return weekIndex === -1 || dayNo === -1 ? null : { weekIndex, dayNo };
};

/** Key of the dashed "Custom · Set hours" card. */
export const CUSTOM_CARD_KEY = "custom";

/** One preset shift card. */
export type ShiftCardOption = { key: string; window: ShiftOptionWindow; shiftIndex: number };

/** Cards from the configured windows, one per distinct window (two roles can configure the same hours). */
export const buildShiftCardOptions = (windows: readonly ShiftOptionWindow[]): ShiftCardOption[] => {
	const byKey = new Map<string, ShiftCardOption>();
	for (const window of windows) {
		const key = `${window.start.toMillis()}-${window.end.toMillis()}`;
		if (!byKey.has(key)) byKey.set(key, { key, window, shiftIndex: getShiftIndexForWindow(window) });
	}
	return [...byKey.values()];
};

/**
 * Which card shows as picked for a window: the preset with exactly those hours, otherwise the Custom
 * card (which then shows the hours). `null` while no shift is picked.
 */
export const resolveSelectedCardKey = (options: readonly ShiftCardOption[], window: ShiftAvailabilityWindow | null): string | null => {
	if (!window) return null;
	// DEFAULT: hours no preset matches are, by definition, custom — the Custom card shows them.
	return options.find((option) => isSameWindow(option.window, window))?.key ?? CUSTOM_CARD_KEY;
};

/* ── Default job ──────────────────────────────────────────────────────────────────────────────────── */

type DefaultJobArgs = { defaultJobId: string | undefined; requestJobId: string | undefined; enabledJobs: readonly Job[] };

/**
 * The job the cards and the list start on: the caller's `defaultJobId` (when the department still has it
 * enabled) → the request's job → the first enabled job. A request's job is honoured even if it has since
 * been disabled — the request itself asks for it. `undefined` = the department has no enabled jobs.
 */
export const resolveDefaultJobId = ({ defaultJobId, requestJobId, enabledJobs }: DefaultJobArgs): string | undefined => {
	if (defaultJobId && enabledJobs.some((job) => job.id === defaultJobId)) return defaultJobId;
	if (requestJobId) return requestJobId;
	// DEFAULT: nothing asked for a job, so start on the first enabled one, in the order the caller passes —
	// most-held first, or alphabetical when there's no popularity signal — resolved PM Q4. `undefined` only
	// when the department has no enabled jobs at all.
	return enabledJobs[0]?.id;
};

/* ── Availability copy ────────────────────────────────────────────────────────────────────────────── */

const REASON_DATE_FORMAT = "MMM d";

/** The "can't work this shift" reason, in the words the row shows. */
export const describeUnavailableReason = (reason: ShiftUnavailableReason, t: TFunction): string => {
	switch (reason.kind) {
		case "leaves_department":
			return t("add.staff.reason.leaves", { date: reason.deactivationDate.toFormat(REASON_DATE_FORMAT) });
		case "on_leave":
			// The stored end date is exclusive — the first day back — so the copy says "back", never "until".
			return reason.endDate ? t("add.staff.reason.on.leave.back", { date: reason.endDate.toFormat(REASON_DATE_FORMAT) }) : t("add.staff.reason.on.leave");
		case "suspended":
			return t("add.staff.reason.suspended");
		case "already_on_shift":
			return t("add.staff.reason.already.on", { hours: formatShiftHours({ start: reason.shiftStart, end: reason.shiftEnd }) });
	}
};

/** One soft badge as the quiet chip beside a selectable row's name. */
const toRowBadge = (badge: ShiftAvailabilityBadge, t: TFunction): NurseRowBadge => {
	switch (badge.kind) {
		case "pto":
			return { kind: badge.kind, label: t("add.staff.badge.pto"), tone: "info", tooltip: t("add.staff.badge.pto.tooltip") };
		case "overtime":
			return { kind: badge.kind, label: t("add.staff.badge.overtime", { hours: roundHours(badge.hours) }), tone: "warning" };
		case "long_day":
			return {
				kind: badge.kind,
				label: t("add.staff.badge.long.day", { hours: roundHours(badge.hours) }),
				tone: "warning",
				tooltip: t("add.staff.badge.long.day.tooltip", { limit: roundHours(badge.limitHours) }),
			};
		case "fully_booked":
			return {
				kind: badge.kind,
				label: t("add.staff.badge.week.shifts", { count: badge.shiftCount, allowed: badge.allowedShifts }),
				tone: "neutral",
				tooltip: t("add.staff.badge.week.shifts.tooltip", { allowed: badge.allowedShifts }),
			};
	}
};

export const buildRowBadges = (badges: readonly ShiftAvailabilityBadge[], t: TFunction): NurseRowBadge[] => badges.map((badge) => toRowBadge(badge, t));

/* ── Footer ───────────────────────────────────────────────────────────────────────────────────────── */

/** The footer's read-back of what the button will do: "Karyn Thomas → Tue, Sep 22 ☀ 3:00 PM–11:00 PM · East Unit". */
export type AddStaffSummary = { who: string; date: string; hours: string | null; location: string | null };

type SummaryArgs = {
	/** Names of the picked staff, in pick order. */
	selectedNames: readonly string[];
	staffingDate: DateTime;
	window: ShiftAvailabilityWindow | null;
	/** The picked location; `""` = no assignment. */
	location: string;
	/** False where the panel has no location picker (hospital), so the summary doesn't claim one. */
	showLocation: boolean;
	t: TFunction;
};

export const buildAddStaffSummary = ({ selectedNames, staffingDate, window, location, showLocation, t }: SummaryArgs): AddStaffSummary => {
	let who: string;
	if (selectedNames.length === 0) who = t("add.staff.summary.pick");
	else if (selectedNames.length === SINGLE_ADD_COUNT) who = selectedNames[0];
	else who = t("add.staff.summary.count", { count: selectedNames.length });
	return {
		who,
		date: formatShiftDate(staffingDate),
		hours: window ? formatShiftHours(window) : null,
		// DEFAULT: `""` is the real "no location" pick, so it reads as "No assignment" rather than a blank.
		location: showLocation ? location || t("add.staff.no.location") : null,
	};
};

/** "Add to shift" for one (or none yet), "Add 3 to shift" for several. */
export const confirmButtonLabel = (selectedCount: number, t: TFunction): string =>
	selectedCount > SINGLE_ADD_COUNT ? t("add.staff.confirm.count", { count: selectedCount }) : t("add.staff.confirm");

/**
 * The picks' summed weekly cost change, or `null` when it can't be stated honestly: nothing picked, or
 * any pick without a numeric prediction (wages hidden, still loading) — a partial sum would under-report.
 * Nurses don't affect each other's overtime, so the per-row figures add up.
 */
export const sumSelectedCost = (rows: readonly NurseData[], selectedIds: readonly number[]): number | null => {
	if (selectedIds.length === 0) return null;
	let total = 0;
	for (const id of selectedIds) {
		const value = rows.find((row) => row.id === id)?.predictedCostValue;
		if (value === undefined) return null;
		total += value;
	}
	return total;
};
